import { createFileRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';

import { VEHICLE_MAINTENANCE_PATH } from '&/features/vehicles/paths';
import { requireModule } from '&/lib/module-gate';

/**
 * The Maintenance tab body (SWOOP-309), rendered inside the vehicle-profile layout at
 * `/vehicles/$vehicleId/overview/maintenance`. Two separate gates, like Settings › Maintenance:
 * - entitlement: a tenant without the Maintenance module gets the standard not-found (the page also
 *   wraps itself in `<ModuleGate>` for a hard refresh);
 * - permission: reading the plan in force needs `service-plans:list`, so a user without it is sent back
 *   to the Overview tab, as the Expenses tab does for `expenses:list`. This guard covers client-side
 *   navigation; on a hard refresh it can run before permissions resolve (RouterProvider never re-runs
 *   `beforeLoad` on that context change), so the page component re-checks `service-plans:list` once they
 *   are ready and performs the same redirect (see `vehicle-maintenance-page.tsx`, like Leasing).
 */
export const Route = createFileRoute('/_authenticated/vehicles_/$vehicleId_/overview/maintenance')({
  beforeLoad: ({ context, params }): void => {
    requireModule(context.entitlement, VEHICLE_MAINTENANCE_PATH);
    if (context.permissions.isReady && !context.permissions.hasPermission('service-plans:list')) {
      throw redirect({ params: { vehicleId: params.vehicleId }, to: '/vehicles/$vehicleId/overview' });
    }
  },
  component: lazyRouteComponent(() => import('&/features/vehicles/pages/vehicle-maintenance-page')),
  staticData: { titleKey: 'vehicleMaintenance' },
});
